import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Briefcase, GraduationCap } from 'lucide-react';

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const [imageRef, imageInView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-[#1a2332] to-[#0f1419] overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <span className="text-sm text-[#c9a961] uppercase tracking-wider mb-4 block">About</span>
          <h2 className="font-serif text-4xl md:text-6xl text-white mb-6 leading-tight">
            Johann C Strauss
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
            International Wealth Protection Attorney & Business Strategy Consultant
          </p>
        </motion.div>

        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          {/* Image */}
          <motion.div
            ref={imageRef}
            initial={{ opacity: 0, x: -60 }}
            animate={imageInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -60 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative group">
              {/* Decorative border */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#c9a961]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              {/* Main image */}
              <div className="relative overflow-hidden">
                <img
                  src="/office-photo-2.webp"
                  alt="Johann C Strauss"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
              </div>

              {/* Gold accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a961] to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
            </div>
          </motion.div>

          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={imageInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 60 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="space-y-6"
          >
            <p className="text-lg text-gray-300 leading-relaxed">
              With over <span className="text-[#c9a961] font-semibold">30 years of experience</span> in international
              wealth protection, banking, and cross-border business strategy, Johann C Strauss provides
              confidential counsel to sophisticated clients worldwide.
            </p>

            <p className="text-lg text-gray-300 leading-relaxed">
              Specializing in <span className="text-[#c9a961] font-semibold">sophisticated drafting and legal engineering at institutional levels</span>,
              his practice focuses on wealth protection and enhancement, international trust and banking structures,
              intricate transaction resolution, and technology law—serving clients across the USA, UK, EU, Middle East,
              Cyprus, and mainland China.
            </p>

            <p className="text-lg text-gray-300 leading-relaxed">
              With advisory involvement exceeding <span className="text-[#c9a961] font-semibold">US$7 trillion</span>,
              Johann brings unparalleled expertise in sophisticated drafting, regulatory navigation, and
              <span className="text-[#c9a961] font-semibold"> confidential, high-priority, top-level negotiation and dealmaking</span>
              &nbsp;for the most demanding international business matters.
            </p>

            {/* Highlights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8">
              <div className="flex items-start gap-3">
                <Briefcase className="w-6 h-6 text-[#c9a961] flex-shrink-0 mt-1" />
                <div>
                  <div className="text-white font-semibold mb-1">30+ Years</div>
                  <div className="text-sm text-gray-400">Legal Practice</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Award className="w-6 h-6 text-[#c9a961] flex-shrink-0 mt-1" />
                <div>
                  <div className="text-white font-semibold mb-1">$7T+</div>
                  <div className="text-sm text-gray-400">Advisory Volume</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <GraduationCap className="w-6 h-6 text-[#c9a961] flex-shrink-0 mt-1" />
                <div>
                  <div className="text-white font-semibold mb-1">Global</div>
                  <div className="text-sm text-gray-400">Expertise</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Secondary image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={imageInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="relative max-w-5xl mx-auto"
        >
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-transparent to-transparent" />
            <img
              src="/office-photo-1.webp"
              alt="Private Consulting Room"
              loading="lazy"
              decoding="async"
              className="w-full h-auto object-cover"
            />
            <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-12">
              <p className="font-serif text-xl md:text-2xl text-white italic">
                "A private consulting room for matters requiring the highest level of discretion and expertise."
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
